import React, { useState } from "react";
import {
  PulseTextField,
  PulseButton,
  PulseCheckbox,
} from "@pulselabs-ai/shared-fe-ui";
import PulseSelect from "../../PulseSelect/PulseSelect";
import "./Step6Invite.scss";

interface TeamMember {
  email: string;
  role: string;
  isSelected: boolean;
}

interface Step6InviteProps {
  teamMembers: TeamMember[];
  onUpdateTeamMembers: (members: TeamMember[]) => void;
}

const Step6Invite: React.FC<Step6InviteProps> = ({
  teamMembers,
  onUpdateTeamMembers,
}) => {
  const [bulkEmails, setBulkEmails] = useState("");
  const [bulkRole, setBulkRole] = useState("");

  const roleOptions = [
    { label: "Owner", value: "owner" },
    { label: "Editor", value: "editor" },
    { label: "Viewer", value: "viewer" },
  ];

  const handleAddToTeam = () => {
    const emails = bulkEmails
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email !== "");

    const newMembers = emails.map((email) => ({
      email,
      role: bulkRole,
      isSelected: false,
    }));

    onUpdateTeamMembers([...teamMembers, ...newMembers]);
    setBulkEmails("");
    setBulkRole("");
  };

  const toggleMemberSelection = (index: number) => {
    const updatedMembers = [...teamMembers];
    updatedMembers[index] = {
      ...updatedMembers[index],
      isSelected: !updatedMembers[index].isSelected,
    };
    onUpdateTeamMembers(updatedMembers);
  };

  const updateTeamMemberRole = (index: number, newRole: string) => {
    const updatedMembers = [...teamMembers];
    updatedMembers[index] = { ...updatedMembers[index], role: newRole };
    onUpdateTeamMembers(updatedMembers);
  };

  return (
    <div className="invite-step">
      <h1 className="pl-heading-xl">Invite team members</h1>
      <p className="pl-body-m description">
        Select which team members to send the SDK documentation to, or invite
        new team members.
      </p>
      <p className="pl-body-s helper-text">
        In order to access the SDK settings, the team member must have Admin or
        Editor permissions.
      </p>

      <div className="invite-form">
        <div className="input-row">
          <div className="text-field-container">
            <PulseTextField
              value={bulkEmails}
              onChange={(e) => setBulkEmails(e.target.value)}
              placeholder="Separate multiple emails with commas"
              fullWidth
              multiline
              rows={4}
            />
          </div>
          <div className="input-column">
            <PulseSelect
              options={roleOptions}
              value={bulkRole}
              onChange={(value) => setBulkRole(value)}
              placeholder="Role"
            />
            <PulseButton
              onClick={handleAddToTeam}
              disabled={!bulkEmails.trim() || !bulkRole}
              fullWidth
            >
              ADD TO TEAM
            </PulseButton>
          </div>
        </div>
      </div>

      {teamMembers.length > 0 && (
        <div className="members-section">
          {teamMembers.map((member, index) => (
            <div key={index} className="member-row">
              <PulseCheckbox
                checked={member.isSelected}
                onChange={() => toggleMemberSelection(index)}
                value={member.isSelected}
              />
              <div className="member-email">{member.email}</div>
              <PulseSelect
                options={roleOptions}
                value={member.role}
                onChange={(value) => updateTeamMemberRole(index, value)}
                placeholder="Role"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Step6Invite;
